import { Fragment, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Dialog, Transition } from '@headlessui/react';
import cx from 'classnames';
import Autocomplete from './autocomplete';

export function SearchModal({ show, hideModal, isLightMode }) {
  const router = useRouter();
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    const handleRouteChange = (url, { shallow }) => {
      if (show) {
        hideModal();
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, show, hideModal]);

  if (!show) {
    return null;
  }

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-y-auto z-[99999]"
        onClose={hideModal}
      >
        <div className="flex items-start justify-center min-h-screen px-4 mt-40 text-center sm:mt-0 sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay
              className={cx(
                'fixed inset-0 transition-opacity bg-opacity-75',
                isLightMode ? 'bg-gray-500' : 'bg-gray-900'
              )}
            />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={cx(
                'relative inline-block align-bottom rounded-lg text-left overflow-x-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-[650px] ',

                isLightMode ? 'bg-white' : 'bg-gray-800'
              )}
            >
              <div
                className={cx(
                  'px-4 pt-5 pb-4 sm:p-6 sm:pb-4',
                  isLightMode ? 'bg-white' : 'bg-gray-800'
                )}
              >
                <Autocomplete
                  placeholder="Search the docs..."
                  autoFocus
                  isLightMode={isLightMode}
                />
              </div>
              <div
                className={cx(
                  'px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse',
                  isLightMode ? 'bg-gray-50' : 'bg-gray-800'
                )}
              >
                <button
                  type="button"
                  className={cx(
                    'inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium border rounded-md shadow-sm  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm uppercase',
                    isLightMode
                      ? 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300'
                      : 'text-gray-300 bg-gray-900 hover:bg-gray-900 border-gray-800'
                  )}
                  onClick={hideModal}
                  ref={cancelButtonRef}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default SearchModal;
